import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { GatewayInstance } from '@vsolv/packages/payments/domain';
import { PaymentGatewayInstanceService } from '@vsolv/packages/payments/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';

interface ProviderFormDefinition {
  label: string;
  accountNameLabel: string;
  accountNamePlaceholder: string;
  secretLabel: string;
  secretPlaceholder: string;
}

const PROVIDER_FORM_DEFINITIONS: Record<GatewayInstance.Provider, ProviderFormDefinition> = {
  [GatewayInstance.Provider.AUTHORIZE_NET]: {
    label: 'Authorize.Net',
    accountNameLabel: 'Account ID',
    accountNamePlaceholder: 'Enter the Authorize.Net account ID',
    secretLabel: 'Transaction key',
    secretPlaceholder: 'Enter the Authorize.Net transaction key',
  },
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-gateway-instance-dialog',
  templateUrl: './gateway-instance.dialog.html',
  standalone: false,
})
export class GatewayInstanceDialog implements OnDestroy {
  constructor(
    private readonly fb: FormBuilder,
    private readonly gatewaySvc: PaymentGatewayInstanceService,
    private readonly toastSvc: ToastService
  ) {}

  @ViewChild(DialogComponent) dialog?: DialogComponent;
  @Output() readonly saved = new EventEmitter<GatewayInstance.Model>();
  @Input() canManage = false;

  readonly providerOptions = Object.values(GatewayInstance.Provider).map(value => ({
    value,
    label: PROVIDER_FORM_DEFINITIONS[value].label,
  }));

  readonly form = this.fb.group({
    name: this.fb.nonNullable.control('', [Validators.required, Validators.pattern(/\S/)]),
    provider: this.fb.nonNullable.control(GatewayInstance.Provider.AUTHORIZE_NET, Validators.required),
    replaceCredentials: this.fb.nonNullable.control(false),
    accountName: this.fb.nonNullable.control(''),
    transactionKey: this.fb.nonNullable.control(''),
  });

  editMode = false;
  loading = false;
  submitting = false;
  selectedGateway: GatewayInstance.Model | null = null;
  private requestGeneration = 0;

  get title(): string {
    return this.editMode ? 'Edit gateway instance' : 'Add gateway instance';
  }

  get subtitle(): string {
    return this.editMode
      ? 'Update the instance name or replace its write-only provider credentials.'
      : 'Add a named payment gateway. Credentials are validated before the gateway becomes active.';
  }

  get providerDefinition(): ProviderFormDefinition {
    return PROVIDER_FORM_DEFINITIONS[this.form.controls.provider.value];
  }

  get providerLabel(): string {
    return this.providerDefinition.label;
  }

  get showCredentialFields(): boolean {
    return !this.editMode || this.form.controls.replaceCredentials.value;
  }

  get canSubmit(): boolean {
    if (!this.canManage || this.loading || this.submitting || this.form.invalid) return false;
    if (!this.editMode) return true;
    if (!this.selectedGateway) return false;

    return (
      this.form.controls.name.value.trim() !== this.selectedGateway.name || this.form.controls.replaceCredentials.value
    );
  }

  openCreate(): void {
    if (!this.canManage) return;
    this.resetForm();
    this.editMode = false;
    this.configureCredentialValidators(true);
    this.dialog?.open();
  }

  async openEdit(id: string): Promise<void> {
    if (!this.canManage) return;
    this.resetForm();
    const requestGeneration = this.requestGeneration;
    this.editMode = true;
    this.loading = true;
    this.dialog?.open();

    try {
      const gateway = await this.gatewaySvc.retrieve(id);
      if (requestGeneration !== this.requestGeneration) return;
      this.selectedGateway = gateway;
      this.form.reset({
        name: gateway.name,
        provider: gateway.provider,
        replaceCredentials: false,
        accountName: '',
        transactionKey: '',
      });
      this.configureCredentialValidators(false);
      this.form.markAsPristine();
    } catch (error: unknown) {
      if (requestGeneration !== this.requestGeneration) return;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to load gateway',
        text: this.readErrorMessage(error, 'Refresh the page and try again.'),
      });
      this.dialog?.close();
      this.resetForm();
    } finally {
      if (requestGeneration === this.requestGeneration) this.loading = false;
    }
  }

  setReplaceCredentials(enabled: boolean): void {
    if (!this.canManage) return;
    this.form.controls.replaceCredentials.setValue(enabled);
    this.configureCredentialValidators(enabled);
    if (!enabled) this.clearCredentials();
  }

  async confirm(): Promise<void> {
    this.form.markAllAsTouched();
    if (!this.canSubmit) return;

    this.submitting = true;
    this.form.disable({ emitEvent: false });

    try {
      const gateway = this.editMode ? await this.updateGateway() : await this.createGateway();
      this.toastSvc.show({
        type: 'success',
        title: this.editMode ? 'Gateway updated' : 'Gateway added',
        text: `${gateway.name} is ready in the payment gateway list.`,
      });
      this.saved.emit(gateway);
      this.dialog?.close();
      this.resetForm();
    } catch (error: unknown) {
      this.toastSvc.show({
        type: 'error',
        title: this.editMode ? 'Unable to update gateway' : 'Unable to add gateway',
        text: this.readErrorMessage(error, 'The gateway could not be saved. Check the details and try again.'),
      });
    } finally {
      // Provider credentials must not remain in component state after a request.
      this.clearCredentials();
      this.submitting = false;
      this.form.enable({ emitEvent: false });
    }
  }

  closeDialog(): void {
    if (this.submitting) return;
    this.dialog?.close();
    this.resetForm();
  }

  resetForm(): void {
    this.requestGeneration += 1;
    this.form.enable({ emitEvent: false });
    this.form.reset({
      name: '',
      provider: GatewayInstance.Provider.AUTHORIZE_NET,
      replaceCredentials: false,
      accountName: '',
      transactionKey: '',
    });
    this.configureCredentialValidators(false);
    this.form.markAsPristine();
    this.selectedGateway = null;
    this.editMode = false;
    this.loading = false;
    this.submitting = false;
  }

  ngOnDestroy(): void {
    this.resetForm();
  }

  formatStatus(status: GatewayInstance.Status): string {
    return status
      .toLowerCase()
      .split('_')
      .map(word => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
      .join(' ');
  }

  private createGateway(): Promise<GatewayInstance.Model> {
    const value = this.form.getRawValue();
    const request: GatewayInstance.CreateRequest = {
      name: value.name.trim(),
      provider: value.provider,
      providerConfig: this.buildProviderConfig(),
    };

    return this.gatewaySvc.create(request);
  }

  private updateGateway(): Promise<GatewayInstance.Model> {
    if (!this.selectedGateway) throw new Error('No gateway selected');

    const value = this.form.getRawValue();
    const request: GatewayInstance.UpdateRequest = {};
    const name = value.name.trim();
    if (name !== this.selectedGateway.name) request.name = name;
    if (value.replaceCredentials) request.providerConfig = this.buildProviderConfig();

    return this.gatewaySvc.update(this.selectedGateway.id, request);
  }

  private buildProviderConfig(): GatewayInstance.ProviderConfig {
    switch (this.form.controls.provider.value) {
      case GatewayInstance.Provider.AUTHORIZE_NET:
        return {
          accountName: this.form.controls.accountName.value.trim(),
          transactionKey: this.form.controls.transactionKey.value.trim(),
        };
    }
  }

  private configureCredentialValidators(required: boolean): void {
    const controls = [this.form.controls.accountName, this.form.controls.transactionKey];
    for (const control of controls) {
      control.setValidators(required ? [Validators.required, Validators.pattern(/\S/)] : []);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  private clearCredentials(): void {
    this.form.controls.accountName.setValue('', { emitEvent: false });
    this.form.controls.transactionKey.setValue('', { emitEvent: false });
    this.form.controls.accountName.markAsPristine();
    this.form.controls.transactionKey.markAsPristine();
  }

  private readErrorMessage(error: unknown, fallback: string): string {
    const message = (error as { error?: { message?: unknown } })?.error?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && message.every(item => typeof item === 'string')) return message.join(' ');
    return fallback;
  }
}
